import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Trans, t } from '@lingui/macro';
import { Button, Radio, RadioGroup, MenuItem, FormControlLabel, Tabs, Tab } from '@material-ui/core';
import _, { extend, compact } from 'lodash';
import { useId } from '../../page-framework/panel/Panel';
import Panel from '../../page-framework/panel/PanelDecorator';
import NgpForm from '../../form/NgpForm';
import FormSection from '../../form/FormSection';
import NgpSelect from '../../form/NgpSelect';
import useChartDisplayType from '../../../utilities/UseChartDisplayType';
import { useDataService } from '../../../services/ApiDataService';
import { useFindById } from '../../../services/FormQueryService';
import { useFormSaveService } from '../../../services/FormSaveService';
import { WebTestNowAgentsFormSection } from './WebTestNowAgentsFormSection';
import { WebTestConfig } from './WebTestConfig';
import { useDetailStyles } from './WebTestNowStyles';
import { isWirelessAgent } from './WebTestNowHelpers';
import { useNavigate } from '../../../utilities/PgmUtilities';
import useBreadcrumb from '../../breadcrumb/UseBreadcrumb';
import { usePageStateValueWithDefault } from '../../../stores/PageStateStore';
import NgpTextField from '../../form/NgpTextField';
import { DEFAULT_INPUT_PROPS } from '../../../utilities/FormUtilities';
import { useUser } from '../../../stores/SessionStore';
import useUiAlerts, { createAlertConfig, WARNING } from '../../../stores/AlertsStore';
import { GET_METHOD, MAX_AGENTS } from './WebTestConstants';
import { buildLinkUrl } from '../../links/Link';
import { QUICK_TEST_STATUS } from '../quick-test-panel/QuickTestConstants';

const testsListApiUrl = '/api/v3/service-tests/list';
const nPointsListApiUrl = '/query/admin/agents/picker';
const quickTestsApiUrl = '/ipm/v1/admin/quicktests/:_id';
const testTypeApiUrl = '/ipm/v1/open/testTypes';

const ADVANCED_TAB = 'advanced';
const EXISTING_TAB = 'existing';
const URL_TAB = 'url';

const OK_200_RESPONSE = '200';

const defaultValues = {
    agents: [],
    testConfig: {
        disableServerCertVerification: false,
        method: GET_METHOD,
        auth: {},
        responseCode: OK_200_RESPONSE,
    },
    tags: [],
    tagLogic: 'any',
    shouldUnregister: false,
};

function WebTestNowDetailPanel({ reRun, tableSelection, submitRef, setBreadcrumbs }) {
    const id = useId();
    const styles = useDetailStyles();
    const [user] = useUser();
    const [, uiAlertActions] = useUiAlerts();
    // Mirror portal/ui UserSession.getUserName(): prefer "first last", fall back to userName, then email.
    const userName = useMemo(() => {
        const firstName = user?.firstName || '';
        const lastName = user?.lastName || '';
        if (firstName && lastName) {
            return `${firstName} ${lastName}`;
        }
        return user?.userName || user?.email || '';
    }, [user]);
    const [testList, setTestList] = useState([]);
    const [agentList, setAgentList] = useState([]);
    const [wirelessAgentList, setWirelessAgentList] = useState([]);
    const [testType, setTestType] = useState([]);
    // Per-interface selection history, mirroring the portal/ui testAgentsPicker's selectedListHistory:
    // each adapter type remembers its own selection independently across switches.
    const selectionHistoryRef = useRef({});
    const [loading, setLoading] = useState(true);
    const [agentSelectionIsDirty, setAgentSelectionIsDirty] = useState(false);
    const methods = useForm({ defaultValues });
    const { reset, setError, setValue, getValues } = methods;
    const formSaveService = useFormSaveService();
    const [interfaceType, setInterfaceType] = useChartDisplayType(`${id}-interfaceType`, 'default');
    const dataService = useDataService();
    const { selection } = tableSelection || null;
    const navigate = useNavigate();
    const [noAgents, setNoAgents] = useState(false);
    const [tooManyAgents, setTooManyAgents] = useState(false);
    const [assignTest, setAssignTest] = useState({});

    const testQueryParams = { testType: 'Web' };
    const testTypeQueryParams = { query: JSON.stringify({ name: 'Web' }) };
    const goToUrl = '/quickTests/web';
    const functionBreadCrumbs = [{ link: goToUrl, text: t`Web Test Now` }];
    const reportingLinkConfig = {
        target: '/quickTests/web',
    };
    const breadcrumbs = useBreadcrumb(functionBreadCrumbs, null, {}, 'name', t`Test Details`, {
        reportingLinkConfig,
    });

    const [activeTab, setActiveTab] = usePageStateValueWithDefault('activeTab', EXISTING_TAB);

    function assignFields(test) {
        setActiveTab(test.quickTestType);
        // _.set(test, 'name', UtilityService.middleEllipsis(_.get(test, 'name'), 70));

        test.copiedFrom = test._id;
        test.createdById = user._id;
        test.createdBy = userName;
        test.adapterType = test.adapterType || 'default';
        // NGP-3233: Set disableServerCertVerification default value to false
        // Which means on UI, Verify Server Certificate = true, checked
        // Work same as testsDetails.controller.js => test.testConfig['disableServerCertVerification'] = false;
        test.testConfig = test.testConfig || {};
        test.testConfig.disableServerCertVerification = test.testConfig.disableServerCertVerification || false;

        return test;
    }

    const getQuickTestById = useCallback(async () => {
        const url = buildLinkUrl({ target: quickTestsApiUrl }, { _id: selection[0] }, null);
        const response = await dataService.get(url, {
            query: JSON.stringify({
                status: _.values(QUICK_TEST_STATUS),
            }),
        });
        return response;
    }, [dataService, selection]);

    useEffect(() => {
        (async () => {
            // Create flow always starts on the Existing Test tab; re-run will override via assignFields below
            if (!reRun) {
                setActiveTab(EXISTING_TAB);
            }

            const { data } = await dataService.get(testsListApiUrl, testQueryParams);
            const agentData = await dataService.get(nPointsListApiUrl);
            const runningAgents = agentData?.filter((agents) => agents?.status.toLowerCase() === 'running');
            const wirelessAgents = runningAgents?.filter((agent) => isWirelessAgent(agent));
            const testTypeData = await dataService.get(testTypeApiUrl, testTypeQueryParams);

            setBreadcrumbs(breadcrumbs);

            // Don't try to run a wireless test on nPoints that don't have a wireless interface or are set to Multi-SSID mode
            setWirelessAgentList(wirelessAgents);
            setAgentList(runningAgents);
            setLoading(false);
            const sortedTests = _.sortBy(data || [], 'name');
            setTestList(sortedTests);
            setTestType(testTypeData[0]);

            // Mirror portal/ui: default-select first existing test, or fall back to the URL tab when there are none.
            // Skip the default selection on re-run so the existing test resolved from the quick test (or the
            // deleted-test warning) isn't overridden by the first list entry.
            if (sortedTests.length) {
                if (!reRun && !getValues('selectedTest')) {
                    setValue('selectedTest', sortedTests[0]);
                }
            } else {
                setActiveTab(URL_TAB);
            }

            // set form fields and required state variables for when a test is chosen to re-run.
            if (reRun && selection) {
                const reRunTestInfo = await getQuickTestById();

                assignFields(reRunTestInfo);

                setAssignTest(reRunTestInfo);

                const { name, adapterType } = reRunTestInfo;

                setValue('name', name);
                setValue('testConfig.url', reRunTestInfo.testConfig.url);
                setInterfaceType(adapterType);

                // This test only currently works with existing tests.
                if (reRunTestInfo.existingTestId) {
                    const theExistingTest = data.find((test) => test.id === reRunTestInfo.existingTestId);
                    if (theExistingTest) {
                        setValue('selectedTest', theExistingTest);
                    } else {
                        // Mirror portal/ui: warn when the referenced existing web test has been deleted,
                        // and leave the Select Existing Test field empty (clear the default first-test selection).
                        setValue('selectedTest', null);
                        uiAlertActions.createAlert(
                            createAlertConfig(
                                WARNING,
                                t`Configuration could not be loaded as this existing web test has been removed`
                            )
                        );
                    }
                }

                // Don't send the test to Offline or Disabled nPoints.
                const runningPreviousAgents = compact(
                    runningAgents.map((agent) => {
                        if (reRunTestInfo.agents.ids.includes(agent._id)) {
                            return agent._id;
                        }
                        // the compact function will remove nulls.
                        return null;
                    })
                );

                // avoid setting previous agents to an empty array.
                if (runningPreviousAgents) {
                    // Seed the history for the re-run's adapter so a later interface switch can restore it.
                    selectionHistoryRef.current[adapterType || 'default'] = runningPreviousAgents;
                    setValue('agents', runningPreviousAgents);
                }
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataService, reRun, tableSelection, setInterfaceType, selection, setValue]);

    // Clear any panel-scoped alerts (e.g. the deleted-existing-test warning) when navigating away.
    useEffect(
        () => () => {
            uiAlertActions.handleRemove();
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const getAgents = (form) => {
        const sourceAgents = interfaceType === 'wireless' ? wirelessAgentList : agentList;
        return sourceAgents?.filter((agent) => form.agents?.includes(agent._id)) || [];
    };

    const dirtyCheck = (isDirty) => isDirty || agentSelectionIsDirty;

    const { initialized, finding, formData } = useFindById({
        url: nPointsListApiUrl,
        resetFunc: reset,
        defaultValues,
    });

    function isActiveTab(value) {
        return value === activeTab;
    }

    const handleFormSubmit = async (submittedFormData) => {
        const agents = getAgents(submittedFormData, agentList, interfaceType);
        // Mirror portal/ui: sort agents by name (case-insensitive) before building the payload
        // so the resulting agents.ids / agentResults arrays are deterministic.
        const sortedAgents = _.sortBy(agents, (agent) => (agent?.name || '').toLowerCase());

        let tformData;
        if (isActiveTab(EXISTING_TAB)) {
            const { selectedTest } = submittedFormData;
            tformData = {
                name: selectedTest.name,
                existingTestId: selectedTest?.id,
                testConfig: { ...selectedTest?.parameters, auth: null },
                type: selectedTest?.testType?.id,
                verify: false,
            };
        } else if (isActiveTab(URL_TAB)) {
            const testConfig = {
                url: submittedFormData.testConfig.url,
                method: GET_METHOD,
                responseCode: OK_200_RESPONSE,
            };
            tformData = {
                name: `Test ${testConfig.url}`,
                beaconPackets: false,
                testConfig,
                type: testType._id,
                verify: false,
            };
        } else if (isActiveTab(ADVANCED_TAB)) {
            const advancedConfig = submittedFormData.testConfig || {};

            tformData = {
                name: submittedFormData.name || `Test ${advancedConfig.url}`,
                beaconPackets: false,
                testConfig: advancedConfig,
                type: testType._id,
                verify: false,
            };
        }

        extend(tformData, {
            adapterType: interfaceType,
            agentResults:
                sortedAgents.map((agent) => ({ agent: agent._id || agent, resultCount: 0, status: 'I' })) || [],
            agents: { all: false, ids: sortedAgents.map((agent) => agent._id || agent) },
            quickTestType: activeTab,
            createdById: user._id,
            createdBy: userName,
        });

        if (reRun && assignTest?._id) {
            tformData.copiedFrom = assignTest._id;
        }

        // Form verification -- don't save the form if there are no nPoints selected.
        const formValues = getValues();
        if (!(formValues.agents?.length || agents.length)) {
            setNoAgents(true);
            setTooManyAgents(false);
            setAgentSelectionIsDirty(false);
            return undefined;
        }

        if (agents.length > MAX_AGENTS) {
            setNoAgents(false);
            setTooManyAgents(true);
            setAgentSelectionIsDirty(false);
            return undefined;
        }

        uiAlertActions.handleRemove();

        // Go straight to results page for this test run on submission
        return formSaveService
            .save({
                url: '/ipm/v1/admin/quicktests',
                dataToSave: tformData,
                setError,
            })
            .then((response) => navigate(`${response._id}`));
    };

    // On interface change, save the current adapter's selection and restore the new adapter's previously saved
    // selection, matching the portal/ui testAgentsPicker's per-adapter history behavior.
    const handleInterfaceChange = (e) => {
        const newInterface = e.target.value;
        const oldInterface = interfaceType;
        const { agents } = getValues();

        if (oldInterface) {
            selectionHistoryRef.current[oldInterface] = agents || [];
        }

        setInterfaceType(newInterface);

        // Restore the previously saved selection for the new interface, filtered to ids still valid
        // for that interface (e.g. an nPoint may have lost wireless capability between switches).
        const validIds = (newInterface === 'wireless' ? wirelessAgentList : agentList)?.map((a) => a._id) ?? [];
        const restored = (selectionHistoryRef.current[newInterface] || []).filter((agentId) =>
            validIds.includes(agentId)
        );
        setValue('agents', restored);
    };

    const handleTabChange = (tab) => {
        if (tab === null) return;

        setActiveTab(tab);
    };

    return (
        <FormProvider {...methods} flexHeight>
            <NgpForm
                formId="web-test-now-form"
                initialized={initialized}
                loading={finding || loading}
                showSave={false}
                formPadding={false}
                showRequiredFieldsTip={false}
                submitFunction={handleFormSubmit}
                isDirtyFunc={dirtyCheck}
            >
                <FormSection title={<Trans>Test Configuration</Trans>}>
                    <Tabs
                        value={activeTab}
                        onChange={(e, tabValue) => handleTabChange(tabValue)}
                        aria-label="web test now detail tabs"
                    >
                        <Tab
                            id="web-test-now-detail-tab-existing-test"
                            value={EXISTING_TAB}
                            label={<Trans>Existing Test</Trans>}
                        />
                        <Tab id="web-test-now-detail-tab-url-test" value={URL_TAB} label={<Trans>URL Test</Trans>} />
                        <Tab
                            id="web-test-now-detail-tab-advanced-est"
                            value={ADVANCED_TAB}
                            label={<Trans>Advanced Test</Trans>}
                        />
                    </Tabs>
                    {activeTab === EXISTING_TAB && (
                        <NgpSelect
                            fieldName="selectedTest"
                            label={t`Select Existing Test`}
                            required
                            className={styles.testSelect}
                            helpTipText="Choose a pre-existing web test to run"
                        >
                            {testList.map((test) => (
                                <MenuItem key={test.id} value={test}>
                                    {test.name}
                                </MenuItem>
                            ))}
                        </NgpSelect>
                    )}
                    {activeTab === URL_TAB && (
                        <NgpTextField
                            fieldName={'testConfig.url'}
                            label={<Trans>URL</Trans>}
                            required
                            inputProps={DEFAULT_INPUT_PROPS}
                        />
                    )}
                    {activeTab === ADVANCED_TAB && (
                        <>
                            <NgpTextField
                                fieldName={'name'}
                                label={<Trans>Advanced Test Name</Trans>}
                                required
                                inputProps={{ size: 35, maxLength: 128 }}
                            />
                            {/* {assignTest.testConfig && <WebTestConfig test={assignTest} setTest={setAssignTest} />} */}
                            {<WebTestConfig test={assignTest} setTest={setAssignTest} />}
                        </>
                    )}
                </FormSection>
                <FormSection title={<Trans>Run On nPoints</Trans>}>
                    <>
                        {noAgents && !agentSelectionIsDirty && (
                            <h3 className={styles.selectionError}>You must select at least one nPoint.</h3>
                        )}
                        {tooManyAgents && !agentSelectionIsDirty && (
                            <h3 className={styles.selectionError}>
                                <Trans>Maximum of {MAX_AGENTS} nPoints allowed.</Trans>
                            </h3>
                        )}
                        <WebTestNowAgentsFormSection
                            formData={formData}
                            setAgentSelectionIsDirty={setAgentSelectionIsDirty}
                            interfaceType={interfaceType}
                            agentList={agentList}
                            wirelessAgentList={wirelessAgentList}
                        />
                    </>
                </FormSection>
                <FormSection title={<Trans>nPoint Interface</Trans>}>
                    <RadioGroup
                        className={styles.radioWrapper}
                        value={interfaceType}
                        onChange={(e) => handleInterfaceChange(e)}
                    >
                        <FormControlLabel value="default" label="Run on nPoint wired interface" control={<Radio />} />
                        <FormControlLabel
                            value="wireless"
                            label={t`Run on hardware nPoint wireless interface`}
                            control={<Radio />}
                        />
                    </RadioGroup>
                </FormSection>
                <Button
                    ref={submitRef}
                    color="primary"
                    variant="contained"
                    size="large"
                    onClick={methods.handleSubmit(handleFormSubmit)}
                >
                    <Trans>Run Test</Trans>
                </Button>
            </NgpForm>
        </FormProvider>
    );
}

export default Panel({
    typeAlias: 'WebTestNowDetailPanel',
    filters: {
        required: [],
        optional: [],
    },
})(WebTestNowDetailPanel);
